import type { HelpLocale } from './help';

/**
 * The Projects tab's strings, in every language the app ships (V0.50.2).
 *
 * `i18n/todos.ts`'s arrangement exactly, and for its reason: the chrome around these strings is
 * hardcoded English in 57 component files, so a French "Projets" between an English "Documents"
 * and an English "Logs" would read as broken rather than translated. The translations are
 * WRITTEN now and English is RESOLVED until the chrome pass (`TRANSLATION-PLAN.md`); the one
 * line that turns them on is marked in `projectText()` below. Strings the To-dos section already
 * has ("Unassigned", "Overdue", the not-yours tooltip, "Show the description") are read from
 * `i18n/todos.ts` rather than repeated here.
 */
export const PROJECT_I18N = {
  /* ---- the tab ---- */
  tab: { en: 'Projects', fr: 'Projets', de: 'Projekte' },
  lead: {
    en: 'A project is a node and everything beneath it: the tasks on those nodes are its work, and every figure here rolls up from the leaves.',
    fr: 'Un projet est un nœud et tout ce qui se trouve en dessous : les tâches de ces nœuds sont son travail, et chaque chiffre remonte depuis les feuilles.',
    de: 'Ein Projekt ist ein Knoten und alles darunter: die Aufgaben dieser Knoten sind seine Arbeit, und jede Zahl hier wird von den Blättern aufsummiert.',
  },
  noProjects: {
    en: 'No project yet — mark a node as a project in its Tasks section.',
    fr: 'Aucun projet pour l’instant — marquez un nœud comme projet dans sa section Tâches.',
    de: 'Noch kein Projekt — markieren Sie einen Knoten in seinem Aufgaben-Abschnitt als Projekt.',
  },
  noProjectsReadOnly: { en: 'No project yet.', fr: 'Aucun projet pour l’instant.', de: 'Noch kein Projekt.' },
  noTodos: {
    en: 'This project has no tasks yet.',
    fr: 'Ce projet n’a pas encore de tâches.',
    de: 'Dieses Projekt hat noch keine Aufgaben.',
  },
  loading: { en: 'Loading…', fr: 'Chargement…', de: 'Wird geladen…' },
  loadError: { en: 'Could not load the projects', fr: 'Impossible de charger les projets', de: 'Die Projekte konnten nicht geladen werden' },
  projectsCount: { en: 'projects', fr: 'projets', de: 'Projekte' },
  projectCount: { en: 'project', fr: 'projet', de: 'Projekt' },
  refresh: { en: 'Re-read', fr: 'Relire', de: 'Neu laden' },
  showInMap: { en: 'Show in map', fr: 'Afficher sur la carte', de: 'Auf der Karte zeigen' },
  nodesBeneath: { en: 'nodes beneath', fr: 'nœuds en dessous', de: 'Knoten darunter' },

  /* ---- the counts ---- */
  open: { en: 'Open', fr: 'Ouvertes', de: 'Offen' },
  done: { en: 'Done', fr: 'Terminées', de: 'Erledigt' },
  overdue: { en: 'Overdue', fr: 'En retard', de: 'Überfällig' },
  dueSoon: { en: 'Due in 7 days', fr: 'Échéance sous 7 jours', de: 'In 7 Tagen fällig' },
  unassigned: { en: 'Unassigned', fr: 'Non attribuées', de: 'Nicht zugewiesen' },

  /* ---- the schedule ---- */
  timeElapsed: { en: 'Time elapsed', fr: 'Temps écoulé', de: 'Verstrichene Zeit' },
  workDone: { en: 'Work done', fr: 'Travail accompli', de: 'Erledigte Arbeit' },
  noSchedule: {
    en: 'No schedule — set a start and a target date in the node’s Tasks section.',
    fr: 'Pas de calendrier — définissez une date de début et une date cible dans la section Tâches du nœud.',
    de: 'Kein Zeitplan — legen Sie im Aufgaben-Abschnitt des Knotens ein Start- und ein Zieldatum fest.',
  },
  noScheduleReadOnly: { en: 'No schedule.', fr: 'Pas de calendrier.', de: 'Kein Zeitplan.' },
  start: { en: 'Start', fr: 'Début', de: 'Start' },
  target: { en: 'Target', fr: 'Cible', de: 'Ziel' },
  today: { en: 'Today', fr: 'Aujourd’hui', de: 'Heute' },
  daysLeft: { en: 'days left', fr: 'jours restants', de: 'Tage übrig' },
  daysOver: { en: 'days over', fr: 'jours de retard', de: 'Tage drüber' },
  dayLeft: { en: 'day left', fr: 'jour restant', de: 'Tag übrig' },
  dayOver: { en: 'day over', fr: 'jour de retard', de: 'Tag drüber' },

  /* ---- the status ---- */
  statusNotStarted: { en: 'Not started', fr: 'Pas commencé', de: 'Nicht begonnen' },
  statusOnTrack: { en: 'On track', fr: 'Dans les temps', de: 'Im Plan' },
  statusAtRisk: { en: 'At risk', fr: 'À risque', de: 'Gefährdet' },
  statusLate: { en: 'Late', fr: 'En retard', de: 'Verspätet' },
  statusDone: { en: 'Done', fr: 'Terminé', de: 'Abgeschlossen' },

  /* ---- the two views ---- */
  viewNodes: { en: 'Nodes', fr: 'Nœuds', de: 'Knoten' },
  viewPeople: { en: 'Assignees', fr: 'Responsables', de: 'Zuständige' },
  nodesHint: {
    en: 'Nodes without tasks are not listed. Click a row for its own tasks.',
    fr: 'Les nœuds sans tâches ne sont pas listés. Cliquez sur une ligne pour ses propres tâches.',
    de: 'Knoten ohne Aufgaben werden nicht aufgeführt. Eine Zeile anklicken zeigt ihre eigenen Aufgaben.',
  },
  peopleHint: {
    en: 'Click a row for that person’s tasks.',
    fr: 'Cliquez sur une ligne pour les tâches de cette personne.',
    de: 'Eine Zeile anklicken zeigt die Aufgaben dieser Person.',
  },
  colNode: { en: 'Node', fr: 'Nœud', de: 'Knoten' },
  colProgress: { en: 'Progress', fr: 'Avancement', de: 'Fortschritt' },
  colDone: { en: 'Done', fr: 'Terminées', de: 'Erledigt' },
  colNextDue: { en: 'Next due', fr: 'Prochaine échéance', de: 'Nächste Fälligkeit' },
  colAssignees: { en: 'Assignees', fr: 'Responsables', de: 'Zuständige' },
  colWho: { en: 'Who', fr: 'Qui', de: 'Wer' },
  colOverdue: { en: 'Overdue', fr: 'En retard', de: 'Überfällig' },
  nestedProject: { en: 'project', fr: 'projet', de: 'Projekt' },
  here: { en: 'here', fr: 'ici', de: 'hier' },
  team: { en: 'team', fr: 'équipe', de: 'Team' },
  noOwnTodos: { en: 'No tasks on this node itself.', fr: 'Aucune tâche sur ce nœud lui-même.', de: 'Keine Aufgaben an diesem Knoten selbst.' },
  noPersonTodos: { en: 'No tasks.', fr: 'Aucune tâche.', de: 'Keine Aufgaben.' },
  unnamedAccount: { en: 'Account', fr: 'Compte', de: 'Konto' },
  unnamedTitle: {
    en: 'The account directory has not loaded',
    fr: 'L’annuaire des comptes n’est pas chargé',
    de: 'Das Kontoverzeichnis ist nicht geladen',
  },
  doneOn: { en: 'done', fr: 'terminée le', de: 'erledigt am' },

  /* ---- the burn-up ---- */
  burnupTitle: { en: 'Burn-up', fr: 'Burn-up', de: 'Burn-up' },
  created: { en: 'Created', fr: 'Créées', de: 'Angelegt' },
  completed: { en: 'Completed', fr: 'Terminées', de: 'Erledigt' },
  burnupHint: {
    en: 'Reconstructed from the tasks that exist today — a deleted task leaves both lines.',
    fr: 'Reconstitué à partir des tâches existantes — une tâche supprimée disparaît des deux courbes.',
    de: 'Aus den heute vorhandenen Aufgaben rekonstruiert — eine gelöschte Aufgabe verschwindet aus beiden Linien.',
  },
  burnupEmpty: { en: 'Nothing to chart yet.', fr: 'Rien à tracer pour l’instant.', de: 'Noch nichts zu zeichnen.' },
  burnupAria: {
    en: 'Burn-up: tasks created and completed per day',
    fr: 'Burn-up : tâches créées et terminées par jour',
    de: 'Burn-up: pro Tag angelegte und erledigte Aufgaben',
  },

  /* ---- the popup's project row ---- */
  badge: { en: 'Project', fr: 'Projet', de: 'Projekt' },
  projectLabel: { en: 'Project', fr: 'Projet', de: 'Projekt' },
  isProject: { en: 'This node is a project', fr: 'Ce nœud est un projet', de: 'Dieser Knoten ist ein Projekt' },
  /** The head's flag button, per state (V0.50.3). */
  markOn: {
    en: 'This node is a project — click to unmark it',
    fr: 'Ce nœud est un projet — cliquez pour retirer la marque',
    de: 'Dieser Knoten ist ein Projekt — klicken, um die Markierung zu entfernen',
  },
  markOff: {
    en: 'Mark this node as a project',
    fr: 'Marquer ce nœud comme projet',
    de: 'Diesen Knoten als Projekt markieren',
  },
  progress: { en: 'Progress', fr: 'Avancement', de: 'Fortschritt' },
  progressTitle: {
    en: 'Open the dashboard on this project',
    fr: 'Ouvrir le tableau de bord sur ce projet',
    de: 'Das Dashboard bei diesem Projekt öffnen',
  },
  startDate: { en: 'Start', fr: 'Début', de: 'Start' },
  targetDate: { en: 'Target', fr: 'Cible', de: 'Ziel' },
  noDates: { en: 'no dates', fr: 'sans dates', de: 'ohne Termine' },
  editDates: {
    en: 'Change the start and target dates',
    fr: 'Modifier les dates de début et de cible',
    de: 'Start- und Zieltermin ändern',
  },
} as const;

export type ProjectKey = keyof typeof PROJECT_I18N;

/**
 * Read one string.
 *
 * THE ONE LINE THAT TURNS TRANSLATION ON — `i18n/todos.ts`'s, repeated: every caller is served
 * English today, whatever it passes, because the chrome around these strings has no locale of
 * its own. When the chrome pass lands, delete the `'en'` below and let `locale` through.
 */
export function projectText(key: ProjectKey, _locale: string): string {
  const s = PROJECT_I18N[key] as Record<string, string>;
  const locale: HelpLocale = 'en';
  return s[locale] ?? s.en;
}
